import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import type { RootState } from '../../../../state/store';
import { useAskVantage } from '../../context/AskVantageProvider';
import { type ThemeConfig, useTheme } from '../../theme';
import ChatView from '../ChatView';
import MainView from '../MainView';
import Sidebar from '../Sidebar';

const Container: React.FC = () => {
  const { messages, sidebarOpen } = useAskVantage();
  const theme = useTheme();
  const companyDetails = useSelector((state: RootState) => state.company?.company_details);
  const hasMaintenanceBanner = companyDetails?.maintenance_mode?.enabled || false;

  // Determine which view to show based on messages
  const showChatView = messages.length > 0;

  return (
    <ContainerWrapper
      hasMaintenanceBanner={hasMaintenanceBanner}
      $theme={theme}
      style={{
        top: hasMaintenanceBanner ? '120px' : '60px',
        height: `calc(100vh - ${hasMaintenanceBanner ? '120px' : '60px'})`
      }}
    >
      <Sidebar />

      <MainContent sidebarOpen={sidebarOpen} $theme={theme}>
        {showChatView ? <ChatView /> : <MainView />}
      </MainContent>
    </ContainerWrapper>
  );
};

const ContainerWrapper = styled.div<{
  hasMaintenanceBanner: boolean;
  $theme: ThemeConfig;
}>`
  /* Container stretches full height minus offset */

  /* Two-column layout */
  display: flex;
  background-color: ${({ $theme }) => $theme.colors.containerBg};
  position: relative;
  overflow: hidden;

  /* Override global font-size for all Ask Vantage content */
  font-size: 16px;

  /* Reset all text elements to reasonable sizes */
  * {
    font-size: inherit;
  }

  h1 {
    font-size: 28px;
  }
  h2 {
    font-size: 24px;
  }
  h3 {
    font-size: 20px;
  }
  h4 {
    font-size: 18px;
  }
  h5 {
    font-size: 16px;
  }
  h6 {
    font-size: 14px;
  }

  p,
  div,
  span,
  button,
  input,
  textarea {
    font-size: 16px;
  }

  small {
    font-size: 14px;
  }
`;

const MainContent = styled.div<{
  sidebarOpen: boolean;
  $theme: ThemeConfig;
}>`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: ${(props) => props.$theme.colors.mainBg};
  color: ${(props) => props.$theme.colors.mainText};

  /* Sidebar interaction effects */
  ${(props) =>
    props.sidebarOpen &&
    `
    padding-top: 10px;
    padding-bottom: 10px;
    border-top-left-radius: ${props.$theme.tokens.radiusLg};
    border-bottom-left-radius: ${props.$theme.tokens.radiusLg};
    margin-top: 10px;
    margin-bottom: 10px;
  `}

  transition: all ${(props) => props.$theme.tokens.transitionFast};

  /* Responsive behavior */
  @media (min-width: 769px) {
    margin-left: ${(props) => (props.sidebarOpen ? '320px' : '0')};
    width: ${(props) => (props.sidebarOpen ? 'calc(100% - 320px)' : '100%')};
  }

  @media (max-width: 1280px) and (min-width: 769px) {
    margin-left: ${(props) => (props.sidebarOpen ? '280px' : '0')};
    width: ${(props) => (props.sidebarOpen ? 'calc(100% - 280px)' : '100%')};
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
    /* Remove padding and radius on mobile */
    padding-top: 0;
    padding-bottom: 0;
    border-radius: 0;
  }
`;

export default Container;
